import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { YesNoCell, DirectCell, CellFunction, NoneGuard } from '../../cells';
import { IEpxHyper } from '../../../../../libs/apps/epxhyper';
import Tooltip from '../../../../../components/organisms/tooltip';
import moment from 'moment';

const BloodPressureCell: CellFunction<IEpxHyper> = ({
  row: { original: session }
}) => {
  const upper = session.upper as number;
  const lower = session.lower as number;

  const upperInvalidation = session?.invalidation?.upper;
  const lowerInvalidation = session?.invalidation?.lower;

  const isUpperInvalidated = !!upperInvalidation;
  const isLowerInvalidated = !!lowerInvalidation;

  const getTooltip = (type: 'upper' | 'lower', invalidation) => {
    if (!invalidation) return '';
    const label = type === 'upper' ? 'Systolic' : 'Diastolic';
    const date = invalidation.createdAt
      ? moment(invalidation.createdAt).format('MMM D, YYYY h:mm A')
      : 'unknown date';
    const prev = invalidation.previousValue ?? '?';
    return `${label} value was invalidated on ${date}. Original value: ${prev}`;
  };

  const upperDisplay = isUpperInvalidated ? (
    <Tooltip content={getTooltip('upper', upperInvalidation)} position="top">
      <span>
        {upperInvalidation.newValue != null ? upperInvalidation.newValue : '?'}*
      </span>
    </Tooltip>
  ) : (
    NoneGuard(upper) || upper
  );

  const lowerDisplay = isLowerInvalidated ? (
    <Tooltip content={getTooltip('lower', lowerInvalidation)} position="top">
      <span>
        {lowerInvalidation.newValue != null ? lowerInvalidation.newValue : '?'}*
      </span>
    </Tooltip>
  ) : (
    NoneGuard(lower) || lower
  );

  return (
    <span>
      {upperDisplay}/{lowerDisplay}
    </span>
  );
};

type Dependencies = [boolean, boolean];

const getDependencies = (sessions: IEpxHyper[]): Dependencies => {
  let askSymptoms = false;
  let askHeartRate = false;
  for (const session of sessions) {
    if (session.hasSymptoms) {
      askSymptoms = true;
    }
    if (session.schedule?.shouldAskHeartRate || session.heartRate != null) {
      askHeartRate = true;
    }
  }
  return [askSymptoms, askHeartRate];
};

const getColumns = (sessions: IEpxHyper[]): Column<IEpxHyper>[] => {
  const [askSymptoms, askHeartRate] = getDependencies(sessions);
  // Compose columns based on variant
  const columns: Column<IEpxHyper>[] = [
    {
      Header: 'Blood Pressure',
      id: 'bp',
      Cell: ResponseGuard(BloodPressureCell)
    },
    {
      Header: 'Took Meds',
      id: 'tookMedication',
      Cell: ResponseGuard(
        YesNoCell('tookMedication') as CellFunction<IEpxHyper>
      )
    }
  ];
  if (askSymptoms) {
    columns.push({
      Header: 'Has Symptoms',
      id: 'hasSymptoms',
      Cell: ResponseGuard(YesNoCell('hasSymptoms') as CellFunction<IEpxHyper>)
    });
  }
  if (askHeartRate) {
    columns.push({
      Header: 'Heart Rate',
      id: 'heartRate',
      Cell: ResponseGuard(DirectCell('heartRate') as CellFunction<IEpxHyper>)
    });
  }
  return columns;
};

const EpxHyperTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxHyper>
      url="/epxhyper/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      appKey="epxhyper"
      invalidationModel="EpxHyper"
      {...props}
    />
  );
};

export default EpxHyperTable;
